import React, { useState, useEffect } from 'react';
import { disputes, flashMessages, modals } from 'ducks';
import { duckify } from "../../utils/duckify";
import ModalComponent from "../ModalComponent";
import ModalRoot from "../ModalRoot";
import { formatCurrency, getMetaContent } from "../../utils";
import { titleize } from "../../utils/strings";
import ControlGroup from "../forms/ControlGroup";
import { formatDateTime } from '../../../react/utils/i18n';
import './styles/Disputes.scss';
import FlashMessage from "../FlashMessage/FlashMessage";
import FlashRoot from "../FlashMessage/FlashRoot";
import saveAs from "file-saver";

const DisputesList = ({ dispatch, disputes }) => {

  const responseStatuses = ['warning_needs_response', 'needs_response']

  return (
    <div>

      <ModalRoot modals={{ DisputeAcceptModal, DisputeShowModal, DisputeCounterModal }} />
      <h1>Disputes</h1>
      <FlashRoot />

      <p>
        <span>
          For more information about disputes and how to respond to them, please refer to the <a href={'https://stripe.com/docs/disputes/responding'} target={'blank'}>Stripe Documentation.</a></span>
        <span> To submit evidence that the dispute is invalid, choose the Counter Dispute option.</span>
        <span> To immediately accept the dispute without contesting it, click Accept Dispute.</span>
        <span className={'error bold'}> Once a dispute is accepted, it is immediately lost and can no longer be contested.</span>
      </p>
      <table className={'table'}>
        <thead>
          <th>Dispute ID</th>
          <th>Order ID</th>
          <th>Transaction Reference</th>
          <th>Customer Name</th>
          <th>Customer Email</th>
          <th>Status</th>
          <th>Reason</th>
          <th>Amount</th>
          <th>Evidence Due By</th>
          <th></th>
          <th></th>

          <th></th>
        </thead>
        <tbody>
          {disputes.map((dispute) =>
            <tr>
              <td>{dispute.external_dispute_id}</td>
              <td><a href={"/setup/manage_orders?search[id]=" + dispute.order_id} target='_blank'>{dispute.order_id}</a></td>
              <td>{dispute.payment_intent_id}</td>
              <td><a href={"/contacts/" + dispute.contact_id + "/billing"}>{dispute.evidence.customer_name}</a></td>
              <td>{dispute.evidence.customer_email_address}</td>
              <td>{titleize(dispute.status)}</td>
              <td>{titleize(dispute.reason)}</td>
              <td>{formatCurrency(dispute.amount / 100)}</td>
              {responseStatuses.includes(dispute.status) ? <td>{formatDateTime(new Date(dispute.evidence_details.due_by * 1000))}</td> : <td />}
              <td><button className='btn btn-primary' onClick={() => dispatch(modals.actions.show('DisputeShowModal', { dispute: dispute }))}>Show</button></td>
              {responseStatuses.includes(dispute.status) ? <td><button style={{ 'white-space': 'nowrap' }} className='btn btn-primary' onClick={() => dispatch(modals.actions.show('DisputeCounterModal', { dispatch: dispatch, dispute: dispute }))}>Counter Dispute</button></td> : <td> </td>}
              {responseStatuses.includes(dispute.status) ? <td><button style={{ 'white-space': 'nowrap' }} className='btn btn-primary' onClick={() => dispatch(modals.actions.show('DisputeAcceptModal', { dispatch: dispatch, dispute: dispute }))}>Accept Dispute</button></td> : <td> </td>}
            </tr>
          )}
        </tbody>
      </table>
    </div>

  )

}


const fetchFileLink = (fileId) => {
  const token = getMetaContent('csrf-token');
  fetch(`/api/disputes/fetch_file?file_id=${fileId}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token }
  })
    .then((response) => {
      if (response.status === 200) {
        return response.json();
      } else {
        throw new Error('Failed to fetch file details');
      }
    })
    .then((data) => {
      const fileUrl = data.dispute_file.url;
      const filename = data.dispute_file.filename || 'downloaded_file.csv';
      fetch(fileUrl, {
        headers: { 'Content-Type': 'application/json' }
      })
        .then((response) => {
          if (response.status === 200) {
            return response.blob();
          } else {
            throw new Error('Failed to fetch file from Stripe');
          }
        })
        .then((blob) => {
          saveAs(blob, filename);
        })
        .catch((error) => {
          console.error('Error fetching file from Stripe:', error);
        });
    })
    .catch((error) => {
      console.error('Error fetching file details:', error);
    });
};

const DisputeShowModal = ({ dispute }) => {
  return <ModalComponent title={"Dispute Information"}>
    <div className={'disputes-show'}>
      <ControlGroup label='Dispute ID'>{dispute.external_dispute_id}</ControlGroup>
      <ControlGroup label='Order Id'><a href={"/setup/manage_orders?search[id]=" + dispute.order_id} target='_blank'>{dispute.order_id}</a></ControlGroup>
      <ControlGroup label='Transaction Reference'>{dispute.payment_intent_id}</ControlGroup>
      <ControlGroup label='Customer Name'><a href={"/contacts/" + dispute.contact_id + "/billing"}>{dispute.evidence.customer_name}</a></ControlGroup>
      <ControlGroup label='Customer Email'>{dispute.evidence.customer_email_address}</ControlGroup>
      <ControlGroup label='Dispute Status'>{titleize(dispute.status)}</ControlGroup>
      <ControlGroup label='Dispute Reason'>{titleize(dispute.reason)}</ControlGroup>
      <ControlGroup label='Dispute Amount'>{formatCurrency(dispute.amount / 100)}</ControlGroup>
      {dispute.evidence.uncategorized_text &&
        <ControlGroup label='Text Evidence'>{dispute.evidence.uncategorized_text}</ControlGroup>}
      {dispute.evidence.customer_communication && <ControlGroup label='Customer Communication'><a href='#' onClick={() => fetchFileLink(dispute.evidence.customer_communication)}>Download Here</a></ControlGroup>}
      {dispute.evidence.customer_signature && <ControlGroup label='Customer Communication'><a href='#' onClick={() => fetchFileLink(dispute.evidence.customer_signature)}>Download Here</a></ControlGroup>}
      {dispute.evidence.uncategorized_file && <ControlGroup label='Additional Evidence'><a href='#' onClick={() => fetchFileLink(dispute.evidence.uncategorized_file)}>Download Here</a></ControlGroup>}
    </div>
  </ModalComponent>
}

const DisputeAcceptModal = ({ dispute, dispatch }) => {
  const buttons = [
    <button className='btn btn-danger' type='button' onClick={() => acceptDispute()}>
      Accept Dispute
    </button>
  ];
  const acceptDispute = () => {
    dispatch(disputes.actions.update({ disputeId: dispute.id, action: 'accept' })).then(() =>
      dispatch(disputes.actions.markDirty()),
      dispatch(modals.actions.hide()),
      dispatch(
        flashMessages.actions.show({
          message: 'The dispute was successfully updated.',
          type: 'success',
          dismissible: true
        })))
      .catch((response) => {
        return dispatch(flashMessages.actions.show({
          message: 'There was an error sending the information to Stripe. ' + response.error,
          type: 'danger',
          dismissible: true
        }))
      })
  }

  return <ModalComponent title={"Accept Dispute"} buttons={buttons}>
    Are you sure you want to accept this dispute?
    You will automatically lose this dispute and will no longer be able to submit evidence that the payment is legitimate.
  </ModalComponent>
}


const DisputeCounterModal = ({ dispute, dispatch }) => {
  const [uncategorizedText, updateText] = useState(dispute.evidence.uncategorized_text || '')
  const [uncategorizedFile, setUncategorizedFile] = useState('')
  const [communicationFile, setCommunicationFile] = useState('')
  const [signatureFile, setSignatureFile] = useState('')
  const [submitting, setSubmitting] = useState('')

  const buttons = [
    <button disabled={submitting} className='btn btn-primary' type='button' onClick={() => updateDispute(false)}>
      Save For Later
    </button>,
    <button disabled={submitting} className='btn btn-primary' type='button' onClick={() => confirmSubmit()}>
      Submit Dispute
    </button>
  ];

  const confirmSubmit = () => {
    dispatch(modals.actions.show('VerificationModal', {
      title: 'Submit Dispute',
      message: "Are you sure you want to submit this dispute? Once the evidence has been submitted, it cannot be modified further.",
      onClose: () => {
        dispatch(modals.actions.hide());
        reject();
      },
      onConfirm: () => {
        dispatch(modals.actions.hide());
        updateDispute(true);
      }
    }))
  }

  const updateDispute = (submit) => {
    setSubmitting(true)
    dispatch(disputes.actions.update({disputeId: dispute.id,
      action: 'update',
      communicationFile,
      uncategorizedFile,
      uncategorizedText,
      signatureFile,
      submit})).then(() => (
      dispatch(disputes.actions.markDirty()),
      dispatch(modals.actions.hide()),
      dispatch(
        flashMessages.actions.show( {
          message:  submit ? 'The dispute was successfully submitted.' : 'The dispute was successfully updated.',
          type: 'success',
          dismissible: true
        }))
    )).catch((response) => {
      setSubmitting(false)
      return dispatch(flashMessages.actions.show( {
        message: 'There was an error sending the information to Stripe. ' + response.error,
        type: 'danger',
        dismissible: true
      }))}
  )
}
  return <ModalComponent title={"Counter Dispute"} buttons={buttons}>
    <p>
      Use this form to provide evidence to counter the dispute. For more information about the dispute categories and what evidence may be best for each type of dispute,
      refer to the <a href={'https://stripe.com/docs/disputes/categories'} target={'blank'}>Stripe documentation on Dispute Categories</a>.
    </p>
    <p>
      To update the details of the dispute, but not submit evidence to the bank, click <span className={'bold'}>Save For Later</span>.
      Once ready to submit, click  <span className={'bold'}>Submit Dispute</span>.  </p>
    <p><span className={'red'}> Once the evidence has been submitted, it cannot be modified further.</span> The status of the review change to Under Review.
      An automated email will be sent when the review process is complete.</p>


    <p></p>

    <form className={'disputes-show'}>
      <ControlGroup label='Customer Name'>{dispute.evidence.customer_name}</ControlGroup>
      <ControlGroup label='Customer Email'>{dispute.evidence.customer_email_address}</ControlGroup>
      <ControlGroup label='Dispute Reason'>{titleize(dispute.reason)}</ControlGroup>
      <ControlGroup label='Additional Information'>
        <textarea id='uncategorized_text' value={uncategorizedText} className='form-control form-control-sm' onChange={(e) => updateText(e.target.value)} />
      </ControlGroup>
      <span className={'error'}>Only one file is allowed for each type of evidence.  Please upload files in the .pdf, .jpeg or .png format. The maximum total upload size is 5 mb.</span>
      <ControlGroup label="Signed Document">
        {dispute.evidence.customer_signature && <span> <a href='#' onClick={() => fetchFileLink(dispute.evidence.customer_signature)}>See Current File</a></span>}
        <input id='customer_signature' className='form-control form-control-sm' type="file" onChange={(e) => setSignatureFile(e.target.files[0])} />
        <div className={'form-text italic'}>Any document or contracts with the customer's signature</div>
      </ControlGroup>

      <ControlGroup label='Customer Communication'>
        {dispute.evidence.customer_communication && <a href='#' onClick={() => fetchFileLink(dispute.evidence.customer_communication)}>See Current File</a>}
        <input className='form-control form-control-sm' type="file" onChange={(e) => setCommunicationFile(e.target.files[0])} />
        <div className={'form-text italic'}>Relevant communication with the customer regarding the dispute</div>
      </ControlGroup>
      <ControlGroup label='Additional Evidence'>
        {dispute.evidence.uncategorized_file && <a href='#' onClick={() => fetchFileLink(dispute.evidence.uncategorized_file)}>See Current File</a>}
        <input className='form-control form-control-sm' type="file" onChange={(e) => setUncategorizedFile(e.target.files[0])} />
        <div className={'form-text italic'}>Any evidence that does not fit into another category</div>
      </ControlGroup>
    </form>
  </ModalComponent>
}

const mapDucksToProps = () => ({
  disputes: disputes
});

export default duckify(mapDucksToProps)(DisputesList);
